import React from "react";
import {
  Activity,
  ArrowRight,
  Award,
  Clock3,
  MapPin,
  ShieldCheck,
  Wind,
} from "lucide-react";
import CameraHrv from "@/components/daily/CameraHrv";
import StateMatchedBreathingGuide from "@/components/daily/StateMatchedBreathingGuide";
import { ENVIRONMENTS, TIME_OPTIONS } from "@/components/daily/dailyState";
import {
  AppCard,
  AppPageHeader,
  ChoiceRow,
  StateCards,
  StateSelector,
} from "@/components/daily/CompanionAppUi";

const readinessOptions = [
  { key: "unrestricted", label: "Unrestricted movement" },
  { key: "light-moderate", label: "Light-to-moderate only" },
  { key: "modify", label: "Movement needs modification" },
  { key: "pause", label: "Exercise should wait" },
];

const supportModeOptions = [
  { key: "auto", label: "Let APOS choose" },
  { key: "movement", label: "Movement / exercise" },
  { key: "breathing", label: "Breathing" },
  { key: "somatic", label: "Low-intensity somatic" },
];

const urgeOptions = [
  { key: "react", label: "React quickly" },
  { key: "withdraw", label: "Withdraw or shut down" },
  { key: "seek-relief", label: "Seek familiar relief" },
  { key: "move", label: "Move or discharge energy" },
  { key: "connect", label: "Reach out to someone" },
  { key: "none", label: "No clear urge" },
];

const ResponseSlider = ({ label, value, onChange, low, high }) => (
  <div>
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="font-medium forr-text-ink">{label}</span>
      <span className="forr-text-teal-deep">{value}/5</span>
    </div>
    <input
      type="range"
      min="1"
      max="5"
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
      className="mt-4 w-full accent-teal-600"
    />
    <div className="mt-2 flex justify-between gap-4 text-[10px] forr-text-mute">
      <span>{low}</span>
      <span className="text-right">{high}</span>
    </div>
  </div>
);

const CompanionCheckInPage = ({
  preferredName,
  checkIn,
  setField,
  biometrics,
  onBiometrics,
  summary,
  validationMessage,
  onCreate,
  breathingProtocol,
  onBreathingFinish,
}) => (
  <div data-testid="companion-check-in-page">
    <AppPageHeader
      eyebrow="Current nervous-system state"
      title={preferredName ? `${preferredName}, what is happening now?` : "What is happening now?"}
      body="Nervous-system state matching uses your emotional experience as context, then matches the physiological output to mobilization, action pressure, usable energy, behavioral choice, body response, attention, recovery, physical capacity, and current training demand."
    />

    {breathingProtocol && (
      <div id="state-matched-breathing-session" className="mb-4">
        <AppCard tone="action">
          <div className="flex items-center gap-3">
            <Wind size={22} className="forr-text-teal-deep" />
            <div>
              <div className="forr-overline">Your selected support</div>
              <h2 className="mt-1 font-display text-2xl forr-text-ink">Follow the breathing circle</h2>
            </div>
          </div>
          <StateMatchedBreathingGuide
            protocol={breathingProtocol}
            onFinish={onBreathingFinish}
          />
        </AppCard>
      </div>
    )}

    <div className="grid gap-4 lg:grid-cols-[1.35fr_0.65fr]">
      <AppCard>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="forr-overline">Nervous-system response</div>
            <h2 className="mt-2 font-display text-2xl forr-text-ink">
              Choose the response that is closest right now
            </h2>
          </div>
          <Activity size={24} className="shrink-0 forr-text-teal-deep" />
        </div>

        <div className="mt-6">
          <StateCards
            primary={checkIn.primaryState}
            secondary={checkIn.secondaryState}
            onPrimary={(value) => setField("primaryState", value)}
            onSecondary={(value) => setField("secondaryState", value)}
          />
          <p className="mt-3 text-xs leading-relaxed forr-text-mute">
            Emotion helps describe the experience. It does not directly determine the exercise dose.
          </p>
        </div>

        <div className="mt-7 border-t pt-6" style={{ borderColor: "var(--forr-line-soft)" }}>
          <div className="text-sm font-medium forr-text-ink">Overall current nervous-system indicator</div>
          <div className="mt-4">
            <StateSelector
              value={checkIn.level}
              onChange={(value) => setField("level", value)}
            />
          </div>
          <p className="mt-3 text-xs forr-text-mute">
            The colour is a visual check-in. The functional response below determines the match to your nervous-system state.
          </p>
        </div>

        <div className="mt-7 grid gap-6 border-t pt-6 sm:grid-cols-2" style={{ borderColor: "var(--forr-line-soft)" }}>
          <ResponseSlider
            label="Mobilization intensity"
            value={checkIn.intensity}
            onChange={(value) => setField("intensity", value)}
            low="Very little"
            high="Extremely strong"
          />
          <ResponseSlider
            label="Usable physical energy"
            value={checkIn.energy}
            onChange={(value) => setField("energy", value)}
            low="Very little"
            high="Readily available"
          />
          <ResponseSlider
            label="Action pressure"
            value={checkIn.actionPressure}
            onChange={(value) => setField("actionPressure", value)}
            low="Little pressure to act"
            high="Very strong pressure"
          />
          <ResponseSlider
            label="Behavioral choice"
            value={checkIn.behavioralChoice}
            onChange={(value) => setField("behavioralChoice", value)}
            low="Very little choice"
            high="Strong choice/control"
          />
          <ResponseSlider
            label="Body-response intensity"
            value={checkIn.somaticResponse}
            onChange={(value) => setField("somaticResponse", value)}
            low="Very little"
            high="Very strong"
          />
          <ResponseSlider
            label="Attention capture"
            value={checkIn.cognitiveCapture}
            onChange={(value) => setField("cognitiveCapture", value)}
            low="Easy to shift attention"
            high="Hard to shift attention"
          />
          <ResponseSlider
            label="Typical recovery difficulty"
            value={checkIn.recoveryDifficulty}
            onChange={(value) => setField("recoveryDifficulty", value)}
            low="Usually transitions quickly"
            high="Usually takes a long time"
          />
        </div>

        <div className="mt-7 border-t pt-6" style={{ borderColor: "var(--forr-line-soft)" }}>
          <div className="text-sm font-medium forr-text-ink">Physical readiness</div>
          <ChoiceRow
            options={readinessOptions}
            value={checkIn.physicalReadiness}
            onChange={(value) => setField("physicalReadiness", value)}
          />
          <p className="mt-3 text-xs leading-relaxed forr-text-mute">
            APOS derives longer-term capacity from your assessment and calendar. Report only what your body can receive right now.
          </p>
        </div>

        <div className="mt-7 border-t pt-6" style={{ borderColor: "var(--forr-line-soft)" }}>
          <div className="flex items-center gap-2 text-sm font-medium forr-text-ink">
            <Wind size={16} /> What kind of support do you want right now?
          </div>
          <ChoiceRow
            options={supportModeOptions}
            value={checkIn.supportMode}
            onChange={(value) => setField("supportMode", value)}
          />
          <p className="mt-3 text-xs leading-relaxed forr-text-mute">
            This does not change your reported nervous-system state. It tells APOS which type of input you are willing and able to use right now. Breathing tempo and somatic work are still matched to the calculated response.
          </p>
        </div>

        <div className="mt-7 grid gap-6 border-t pt-6 sm:grid-cols-2" style={{ borderColor: "var(--forr-line-soft)" }}>
          <div>
            <div className="flex items-center gap-2 text-sm font-medium forr-text-ink">
              <MapPin size={16} /> Environment
            </div>
            <ChoiceRow
              options={ENVIRONMENTS}
              value={checkIn.environment}
              onChange={(value) => setField("environment", value)}
            />
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm font-medium forr-text-ink">
              <Clock3 size={16} /> Time available now
            </div>
            <ChoiceRow
              options={TIME_OPTIONS}
              value={checkIn.minutes}
              onChange={(value) => setField("minutes", value)}
            />
          </div>
        </div>

        <div className="mt-7 grid gap-5 border-t pt-6 sm:grid-cols-2" style={{ borderColor: "var(--forr-line-soft)" }}>
          <label className="text-sm forr-text-soft">
            Hydration
            <select
              value={checkIn.hydration}
              onChange={(event) => setField("hydration", event.target.value)}
              className="mt-2 block w-full rounded-xl border border-black/10 bg-white p-3 forr-text-ink"
            >
              <option value="">Select</option>
              <option value="ready">Adequately hydrated</option>
              <option value="some">Some fluid</option>
              <option value="little">Very little</option>
              <option value="unsure">Unsure</option>
            </select>
          </label>
          <label className="text-sm forr-text-soft">
            Nourishment
            <select
              value={checkIn.nourishment}
              onChange={(event) => setField("nourishment", event.target.value)}
              className="mt-2 block w-full rounded-xl border border-black/10 bg-white p-3 forr-text-ink"
            >
              <option value="">Select</option>
              <option value="ready">Adequately nourished</option>
              <option value="light">Only light intake</option>
              <option value="not-recent">Not eaten recently</option>
              <option value="unsure">Unsure</option>
            </select>
          </label>
        </div>

        <div className="mt-7 border-t pt-6" style={{ borderColor: "var(--forr-line-soft)" }}>
          <div className="text-sm font-medium forr-text-ink">Likely action direction</div>
          <ChoiceRow
            options={urgeOptions}
            value={checkIn.urge}
            onChange={(value) => setField("urge", value)}
          />
        </div>

        {validationMessage && (
          <p className="mt-5 text-sm" style={{ color: "#a54242" }}>
            {validationMessage}
          </p>
        )}

        <button
          type="button"
          onClick={onCreate}
          className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-medium forr-btn-primary sm:w-auto"
        >
          Match my physiological output <ArrowRight size={17} />
        </button>
      </AppCard>

      <div className="space-y-4">
        <CameraHrv onReading={onBiometrics} />

        <AppCard tone="information">
          <div className="flex items-center gap-3">
            <Award size={21} className="forr-text-teal-deep" />
            <div>
              <div className="text-xs forr-text-mute">Lifetime reward points</div>
              <div className="font-display text-2xl forr-text-ink">{summary.points}</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-4 gap-2">
            {summary.badges.map((badge) => (
              <div
                key={badge.key}
                title={badge.label}
                className="rounded-xl p-2 text-center"
                style={{
                  backgroundColor: badge.earned ? "var(--forr-teal-tint)" : "var(--forr-canvas-alt)",
                  opacity: badge.earned ? 1 : 0.42,
                }}
              >
                <div className="text-xl">{badge.emoji}</div>
                <div className="mt-1 text-[9px] forr-text-mute">{badge.label}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs leading-relaxed forr-text-mute">
            Rewards recognize honest participation and response tracking—not a particular nervous-system state.
          </p>
        </AppCard>

        <AppCard tone="information">
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="mt-0.5 forr-text-teal-deep" />
            <div>
              <div className="font-medium forr-text-ink">Nervous-system state matching + nervous-system training</div>
              <p className="mt-2 text-xs leading-relaxed forr-text-mute">
                {preferredName || "You"} reports the experience and functional response. APOS uses behavioral choice and physical capacity as ceilings, then accounts for hydration, nourishment, environment, current calendar demand, and prior observed responses. Repeated matching and recovery practice is the nervous-system training component of regulation.
              </p>
            </div>
          </div>
        </AppCard>

        {biometrics && (
          <AppCard tone="information">
            <div className="forr-overline">Latest optional reading</div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="rounded-xl p-3 forr-surface-tint">
                <div className="font-display text-xl forr-text-ink">
                  {biometrics.heartRate || biometrics.bpm || "—"}
                </div>
                <div className="text-[10px] forr-text-mute">Heart rate</div>
              </div>
              <div className="rounded-xl p-3 forr-surface-tint">
                <div className="font-display text-xl forr-text-ink">
                  {biometrics.hrvMs ? Math.round(biometrics.hrvMs) : "—"}
                </div>
                <div className="text-[10px] forr-text-mute">HRV estimate</div>
              </div>
            </div>
          </AppCard>
        )}
      </div>
    </div>
  </div>
);

export default CompanionCheckInPage;
